/**
 * WAF custom pages — the customer's catalog of reusable error/challenge pages
 * (block / 5xx / rate-limit / JS / captcha / maintenance) that domains reference
 * by id. Service-scoped, backed by
 * `GET|POST /services/:service_id/waf/custom-pages` and
 * `PATCH|DELETE /services/:service_id/waf/custom-pages/:id`.
 *
 * The list endpoint has no server-side search/filter (page/page_size only). The
 * create/update response does NOT echo back `url`/`token`, so the edit form can
 * only pre-fill name + type.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  unwrapList,
  type ListResult,
} from './_shared';

/** One custom page (json mirrors BE `providers.CustomPage`). `content` is the
 *  base64-rendered HTML built by the platform from the source URL (read-only). */
export interface WafCustomPage {
  id: number;
  name: string;
  /** Vendor token: `block | 5xx | rateLimit | js | captcha | maint`. */
  type: string;
  /** Source page URL the platform renders (echoed back by the list endpoint). */
  url?: string;
  content: string;
}

/** Create/update body (`providers.CustomPageInput`). `url` is the source page the
 *  platform fetches to render; `token` is required only for `js` / `captcha`. */
export interface WafCustomPageInput {
  type: string;
  name: string;
  url: string;
  token?: string;
}

export interface WafCustomPageListFilters {
  page?: number;
  page_size?: number;
}

/** List a service's custom pages (paginated; no server-side search/filter). */
export function useWafCustomPages(
  serviceId: string | undefined,
  filters: WafCustomPageListFilters = {},
) {
  const params = { ...filters };
  return useQuery<ListResult<WafCustomPage>>({
    queryKey: wafKeys.customPages(serviceId ?? '', filters as Record<string, unknown>),
    enabled: !!serviceId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<WafCustomPage>(await serviceApiV3.get(`/services/${serviceId}/waf/custom-pages`, { params })),
  });
}

/** Create a custom page (`POST /waf/custom-pages`). */
export function useCreateWafCustomPage(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafCustomPage, Error, WafCustomPageInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WafCustomPageInput) =>
      unwrap<WafCustomPage>(
        await serviceApiV3.post(`/services/${serviceId}/waf/custom-pages`, body),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'custom-pages'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a custom page by id (`PATCH /waf/custom-pages/:id`). */
export function useUpdateWafCustomPage(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafCustomPage, Error, { id: number; input: WafCustomPageInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: WafCustomPageInput }) =>
      unwrap<WafCustomPage>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/custom-pages/${id}`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'custom-pages'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a custom page by id (`DELETE /waf/custom-pages/:id`). */
export function useDeleteWafCustomPage(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/custom-pages/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'custom-pages'] });
      options?.onSuccess?.(...args);
    },
  });
}
